import React, { useState } from "react";
import CustomTable from "../../components/Shared/CustomTable";
import TableLoading from "../../components/Shared/TableLoading";
import { useTranslation } from "react-i18next";
import { formatDate } from "../Transactions/TransactionsTable";
import { DeleteIcon, EditIcon } from "../../components/Shared/TableActionIcons";

import CustomPagination from "../../components/Shared/CustomPagination";
import ConfirmationModal from "../../components/Shared/ConfirmationModal";
import { FiTrash2 } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import validSupergitHIS from "../../api/validSupergitHIS";
import toast from "react-hot-toast";

function PaymentMethodsTable({
  listData,
  isLoading,
  onPageChange,
  onRefresh,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [confirmationDialogVisible, setConfirmationDialogVisible] =
    useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const sequenceBodyTemplate = (rowData, options) => {
    return options.rowIndex + 1 + (listData?.page - 1) * listData?.per_page;
  };

  const templateBody = (rowData) => {
    return (
      <div className="flex gap-1">
        <EditIcon
          onClick={() => {
            navigate("/manager/payment/methods/create", {
              state: { editData: rowData },
            });
          }}
          title={t("edit")}
        />
        <DeleteIcon
          onClick={() => {
            setConfirmationDialogVisible(true);
            setSelectedData(rowData);
          }}
          title={t("delete")}
        />
      </div>
    );
  };

  const columnsData = [
    {
      body: templateBody,
      header: t("actions"),
    },
    {
      body: sequenceBodyTemplate,
      header: t("no"),
      style: { width: "3rem" },
    },
    {
      field: "name_en",
      header: t("name_en"),
    },
    {
      field: "name_ar",
      header: t("name_ar"),
    },
    {
      field: "short_name",
      header: t("short_name"),
    },
    {
      field: "created_at",
      header: t("createdAt"),
      body: (rowData) => formatDate(rowData.created_at),
    },
  ];

  const handlePageChange = async (newPage, newRowsPerPage) => {
    if (onPageChange) {
      onPageChange(newPage, newRowsPerPage);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await validSupergitHIS.delete(`payment_methods/${selectedData?.id}`);
      if (res?.data) {
        toast.success(res?.data?.message || t("Deleted successfully"));
        setConfirmationDialogVisible(false);
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      console.error(err);
      toast.error(t("Failed to delete"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <CustomTable
        isLoading={isLoading || deleting}
        loadingIcon={<TableLoading />}
        data={listData?.data}
        columns={columnsData}
        rows={listData?.per_page}
        rootyStyles="max-h-[50vh] overflow-auto"
      />

      {listData?.data?.length > 0 && (
        <CustomPagination
          currentPage={listData?.page}
          rowsPerPage={listData?.per_page}
          totalRecords={listData?.total_pages}
          totalRecord={listData?.total_record}
          onPageChange={handlePageChange}
        />
      )}

      <ConfirmationModal
        message={`${t("do_you_really_want_to_delete_this_payment_method")} "${selectedData?.name_en}"?`}
        header={t("delete_confirmation")}
        Icon={FiTrash2}
        isLoading={deleting}
        visible={confirmationDialogVisible}
        setVisible={setConfirmationDialogVisible}
        onAccept={handleDelete}
        acceptLabel={t("delete")}
        rejectLabel={t("cancel")}
      />
    </>
  );
}

export default PaymentMethodsTable;
